<template>
    <v-card>
        <v-card-title class="blue-grey lighten-5 primary--text">
            Select language
        </v-card-title>

        <v-card-text class="py-3">

            <div class="d-flex justify-center" style="border: 1px solid #a9a9a9; padding: 8px; border-radius: 8px;">
                <span style="font-size: 16px;">Multilingual (auto-detect)</span>
            </div>

            <div class="d-flex justify-center" style="margin: 8px 0 8px 0;">
                <v-icon style="font-size: 56px; text-align: center;">mdi-arrow-down-thin</v-icon>
            </div>

            <v-select
                v-model="targetLanguage"
                :items="languageOptions"
                item-title="label"
                item-value="value"
                label="Target language"
                variant="outlined"
                density="comfortable"
                hide-details
            ></v-select>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="close" color="error" variant="text">
                Cancel
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn @click="clickPost" :loading="btnLoading" color="primary">
                Translate
            </v-btn>
            <v-spacer></v-spacer>
        </v-card-actions>

    </v-card>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');

import ApiTranslation from '@/api/ApiTranslation';
import { Toast } from '@/service/SweetAlert';

const TRANSLATION_CACHE_PREFIX = 'translation_';
const MAX_CACHE_ENTRIES = 100;

const LANGUAGE_OPTIONS = [
    { label: 'ភាសាខ្មែរ', value: 'km' },
    { label: '日本語', value: 'ja' },
    { label: 'English', value: 'en' },
];

interface DataType {
    btnLoading: boolean;
    targetLanguage: string;
    languageOptions: typeof LANGUAGE_OPTIONS;
}

export default defineComponent({
    name: 'TranslationModal',

    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        childId: {
            type: Number,
            default: NaN,
        },
        selectedChat: {
            type: Object as PropType<Record<string, any> | null>,
            default: null,
        },
    },

    data: (): DataType => ({
        btnLoading: false,
        targetLanguage: 'km',
        languageOptions: LANGUAGE_OPTIONS,
    }),

    methods: {
        close() {
            this.$emit('close');
        },
        getCacheKey(chatId: number): string {
            return `${TRANSLATION_CACHE_PREFIX}${chatId}`;
        },
        getCachedTranslation(chatId: number, lang: string): string | null {
            try {
                const raw = localStorage.getItem(this.getCacheKey(chatId));
                if (!raw) return null;
                const cache = JSON.parse(raw);
                const text = cache.translations?.[lang] ?? null;
                if (text) {
                    // 利用したエントリの日時を更新し、削除対象になりにくくする
                    cache.updatedAt = Date.now();
                    localStorage.setItem(this.getCacheKey(chatId), JSON.stringify(cache));
                }
                return text;
            } catch {
                return null;
            }
        },
        saveCachedTranslation(chatId: number, lang: string, text: string) {
            try {
                const raw = localStorage.getItem(this.getCacheKey(chatId));
                const parsed = raw ? JSON.parse(raw) : null;
                const translations = parsed?.translations ?? {};
                translations[lang] = text;
                localStorage.setItem(
                    this.getCacheKey(chatId),
                    JSON.stringify({ translations, updatedAt: Date.now() }),
                );
                this.pruneTranslationCache();
            } catch (error) {
                console.error('Translation cache save error:', error);
            }
        },
        pruneTranslationCache() {
            const entries: Array<{ key: string; updatedAt: number }> = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key || !key.startsWith(TRANSLATION_CACHE_PREFIX)) continue;
                let updatedAt = 0;
                try {
                    updatedAt = JSON.parse(localStorage.getItem(key) ?? '{}').updatedAt ?? 0;
                } catch {
                    // 壊れたエントリは最古扱いで削除対象にする
                }
                entries.push({ key, updatedAt });
            }
            if (entries.length <= MAX_CACHE_ENTRIES) return;
            entries.sort((a, b) => a.updatedAt - b.updatedAt);
            entries
                .slice(0, entries.length - MAX_CACHE_ENTRIES)
                .forEach((entry) => localStorage.removeItem(entry.key));
        },
        async clickPost() {
            if (this.selectedChat) {
                this.btnLoading = true;
                const targetLanguage = this.targetLanguage;
                const cacheLang = `auto-${targetLanguage}`;
                const chatId = this.selectedChat.chatId;
                try {
                    const cached = this.getCachedTranslation(chatId, cacheLang);
                    if (cached) {
                        this.$emit('translate', this.selectedChat, cached);
                        Toast.fire({
                            icon: 'success',
                            title: '翻訳が完了しました',
                        });
                        this.$emit('close');
                        return;
                    }

                    const response = await ApiTranslation.translateText({
                        text: this.selectedChat.body,
                        //sourceLanguage: 'auto',
                        targetLanguage,
                    });

                    this.saveCachedTranslation(chatId, cacheLang, response.translatedText);
                    this.$emit('translate', this.selectedChat, response.translatedText);
                    Toast.fire({
                        icon: 'success',
                        title: '翻訳が完了しました',
                    });
                    this.$emit('close');
                } catch (error) {
                    console.error('Translation error:', error);
                    Toast.fire({
                        icon: 'error',
                        title: '翻訳に失敗しました',
                    });
                } finally {
                    this.btnLoading = false;
                }
            }
        },
    },
});
</script>
<style lang="scss" scoped></style>
